import Map, { type MapRef } from "@vis.gl/react-maplibre";
import type { Map as MapLibreMap } from "maplibre-gl";
import { useId, useRef, useState, type FC } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PIC } from "@ryde/assets";
import {
  DEFAULT_TILES_SERVICE,
  defaultMapCenter,
  systemSettingsDtoAtom,
  TilesServiceEnum,
} from "@ryde/shared";

import { MAP_INITIAL_ZOOM, MAP_MAX_ZOOM, MAP_MIN_ZOOM } from "../map.constants";
import { MapLibreControls } from "./maplibre-controls";
import { MapLibreGoogleAttribution } from "./maplibre-google-attribution";
import { MapLibreLayerControl } from "./maplibre-layer-control";
import type { IMapLibreMapHostProps } from "./maplibre-map-host.models";
import { StyledMapLibreMapHost } from "./maplibre-map-host.styled";
import { MapLibrePlaceInfo } from "./maplibre-place-info";
import { MapLibrePlaceInfoContext } from "./maplibre-place-info.context";
import { MapLibreWebGLUnavailable } from "./maplibre-webgl-unavailable";
import { MapLibreZoomProvider } from "./maplibre-zoom-provider";
import { useMapLibreCanRender } from "./use-maplibre-can-render";
import { useMapLibreControlTooltips } from "./use-maplibre-control-tooltips";
import { useMapLibreHostHasSize } from "./use-maplibre-host-has-size";
import { useMapLibreHostPlaceInfo } from "./use-maplibre-host-place-info";
import { useMapLibreMapLocale } from "./use-maplibre-map-locale";
import { useMapLibreRasterStyle } from "./use-maplibre-raster-style";

/**
 * The one MapLibre map shell every map in the app mounts.
 *
 * Owns everything that is the same on all of them — raster tiles from the `tilesService` system param,
 * the base-tile selector, the zoom control with translated tooltips, the zoom provider, and the Google
 * attribution logo — so a screen only supplies its own layers. Fullscreen is opt-in, since most maps never
 * had it.
 *
 * It exists because that chrome had already been hand-copied once (the ride investigation map) and had
 * drifted: that copy shipped without translated control tooltips and without the Google logo. Anything
 * added here reaches every map at once.
 */
export const MapLibreMapHost: FC<IMapLibreMapHostProps> = ({
  children,
  header,
  mapRef,
  onLoadedChange,
  onMapClick,
  initialCenter = defaultMapCenter,
  initialZoom = MAP_INITIAL_ZOOM,
  minZoom = MAP_MIN_ZOOM,
  maxZoom = MAP_MAX_ZOOM,
  isHidden,
  forcedTilesService,
  isLayerControlHidden,
  areControlsHidden,
  isPlaceInfoHidden,
  isFullscreenEnabled,
  isAttributionHidden,
  isZoomEnabled = true,
  isDragPanEnabled = true,
  className,
  ...styledAttributes
}) => {
  const internalMapRef = useRef<MapRef>(null);
  const resolvedMapRef = mapRef ?? internalMapRef;
  const [hostElement, setHostElement] = useState<HTMLDivElement | null>(null);
  const hasHostSize = useMapLibreHostHasSize(hostElement);
  const canRenderMap = useMapLibreCanRender();
  // Stable id for the outer host so FullscreenControl fullscreens the whole host (incl. the layer
  // control, the Google logo and any `header`), not just the inner map canvas.
  const hostId = `maplibre-host-${useId().replace(/:/g, "")}`;
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  /*
   * Every map here is north-up, as all of them were on Leaflet, which cannot rotate at all. MapLibre can:
   * a two-finger twist and the arrow keys both change the bearing, and the compass that would reset it is
   * hidden. Rotation is switched off on the handlers that own it — the drag case is a prop below — rather
   * than by hiding the symptom, so pinch-to-zoom keeps working while the twist does nothing.
   */
  const handleMapLoad = useEvent((event: { target: MapLibreMap }) => {
    event.target.touchZoomRotate?.disableRotation();
    event.target.keyboard?.disableRotation();
    setIsMapLoaded(true);
    onLoadedChange?.(true);
  });
  // No onRemove setState: MapLibre fires `remove` inside Map.remove(), and a React update
  // would unmount markers while painter.destroy() is still running.

  const handleMapClick = useEvent(
    (event: { lngLat: { lat: number; lng: number } }) => {
      onMapClick?.({ lat: event.lngLat.lat, lng: event.lngLat.lng });
    },
  );

  const {
    isPlaceInfoShown,
    isPlaceInfoActive,
    setIsPlaceInfoActive,
    isPickingPlace,
    placeInfoContext,
  } = useMapLibreHostPlaceInfo({ areControlsHidden, isPlaceInfoHidden });

  const [selectedTilesService, setSelectedTilesService] =
    useState<TilesServiceEnum | null>(null);
  const systemSettings = useRecoilValue(systemSettingsDtoAtom);
  const effectiveTilesService =
    forcedTilesService ??
    selectedTilesService ??
    systemSettings?.metadata?.tilesService ??
    DEFAULT_TILES_SERVICE;
  // A pinned provider opted out of the app-wide choice, so it also opts out of Google's attribution UI —
  // otherwise a decorative preview would keep fetching viewport copyright it never shows.
  const isGoogleTilesActive =
    !forcedTilesService && effectiveTilesService === TilesServiceEnum.Google;
  const mapStyle = useMapLibreRasterStyle(
    forcedTilesService ?? selectedTilesService ?? undefined,
  );
  const mapLocale = useMapLibreMapLocale();

  useMapLibreControlTooltips(resolvedMapRef, isMapLoaded);

  return (
    <StyledMapLibreMapHost
      {...styledAttributes}
      ref={setHostElement}
      $hiddenState={isHidden}
      $isPlaceInfoShown={isPlaceInfoShown}
      $isPickingPlace={isPickingPlace}
      id={hostId}
      className={className}
    >
      {header}

      {canRenderMap && hasHostSize && (
        <Map
          ref={resolvedMapRef}
          mapStyle={mapStyle}
          initialViewState={{
            longitude: initialCenter.lng,
            latitude: initialCenter.lat,
            zoom: initialZoom,
          }}
          minZoom={minZoom}
          maxZoom={maxZoom}
          maxPitch={0}
          locale={mapLocale}
          attributionControl={isAttributionHidden ? false : { compact: true }}
          scrollZoom={isZoomEnabled}
          dragPan={isDragPanEnabled}
          doubleClickZoom={isZoomEnabled}
          touchZoomRotate={isZoomEnabled}
          dragRotate={false}
          onLoad={handleMapLoad}
          {...(onMapClick && !isPickingPlace
            ? { onClick: handleMapClick }
            : {})}
        >
          {!areControlsHidden && (
            <MapLibreControls
              containerId={hostId}
              isFullscreenEnabled={isFullscreenEnabled}
            />
          )}

          {isPlaceInfoShown && (
            <MapLibrePlaceInfo
              isActive={isPlaceInfoActive}
              onActiveChange={setIsPlaceInfoActive}
            />
          )}

          {isGoogleTilesActive && <MapLibreGoogleAttribution />}

          <MapLibrePlaceInfoContext.Provider value={placeInfoContext}>
            <MapLibreZoomProvider>{children}</MapLibreZoomProvider>
          </MapLibrePlaceInfoContext.Provider>
        </Map>
      )}

      {!canRenderMap && <MapLibreWebGLUnavailable />}

      {!isLayerControlHidden && canRenderMap && (
        <MapLibreLayerControl
          selectedTilesService={selectedTilesService}
          onSelectTilesService={setSelectedTilesService}
        />
      )}

      {isGoogleTilesActive && canRenderMap && (
        <div className="google-maps-logo">
          <PIC.GoogleMapsLogoWithDarkOutline />
        </div>
      )}
    </StyledMapLibreMapHost>
  );
};
